import { LoanType } from "@/types";
import { 
  Home, 
  Building2, 
  Users, 
  Briefcase, 
  CreditCard, 
  Building, 
  FileCheck,
  Target,
  TrendingUp,
  Sparkles,
  User,
  GraduationCap,
  BookOpen,
  Award
} from "lucide-react";

export interface QuestionnaireField {
  id: string;
  type: 'text' | 'number' | 'select' | 'radio' | 'checkbox' | 'date' | 'currency' | 'textarea';
  label: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
  conditional?: (data: any) => boolean;
}

export interface QuestionnaireSection {
  id: string;
  title: string;
  icon: any;
  description?: string;
  fields: QuestionnaireField[];
  conditional?: (data: any) => boolean;
}

export interface LoanQuestionnaire {
  loanType: LoanType;
  sections: QuestionnaireSection[];
  requiredDocuments: string[];
}

// Helper function to safely check if employmentType includes 'self_employed'
const isSelfEmployed = (data: any): boolean => {
  return data.employmentType?.includes('self_employed') ?? false;
};

// Home Loan Questionnaire Configuration
const homeLoanQuestionnaire: LoanQuestionnaire = {
  loanType: 'home_loan',
  sections: [
    {
      id: 'property_details',
      title: 'Property Details',
      icon: Home,
      description: 'Tell us about the property you want to finance',
      fields: [
        {
          id: 'propertyType',
          type: 'radio',
          label: 'Property Type',
          validation: { required: true },
          options: [
            { value: 'residential_apartment', label: 'Residential Apartment' },
            { value: 'independent_house', label: 'Independent House' },
            { value: 'villa', label: 'Villa' },
            { value: 'plot', label: 'Plot/Land' },
            { value: 'commercial', label: 'Commercial Property' }
          ]
        },
        {
          id: 'propertyStatus',
          type: 'radio',
          label: 'Property Status',
          validation: { required: true },
          options: [
            { value: 'ready_to_move', label: 'Ready to Move' },
            { value: 'under_construction', label: 'Under Construction' },
            { value: 'land', label: 'Land/Plot Only' }
          ]
        },
        {
          id: 'propertyValue',
          type: 'currency',
          label: 'Property Value (₹)',
          placeholder: 'Ex - 50,00,000',
          validation: { required: true, min: 100000 }
        },
        {
          id: 'propertyAge',
          type: 'number',
          label: 'Property Age (Years)',
          placeholder: 'Ex - 5',
          conditional: (data) => data.propertyStatus !== 'land'
        },
        {
          id: 'propertyAddress',
          type: 'textarea',
          label: 'Property Address',
          placeholder: 'Complete address with landmark',
          validation: { required: true, minLength: 10 }
        },
        {
          id: 'propertyCity',
          type: 'text',
          label: 'Property City',
          placeholder: 'Ex - Mumbai',
          validation: { required: true }
        },
        {
          id: 'propertyPincode',
          type: 'text',
          label: 'Property Pincode',
          placeholder: 'Ex - 400001',
          validation: { required: true, pattern: '^[0-9]{6}$' }
        },
        {
          id: 'builtUpArea',
          type: 'number',
          label: 'Built-up Area (sq.ft)',
          placeholder: 'Ex - 1200',
          conditional: (data) => data.propertyStatus !== 'land',
          validation: { min: 100 }
        },
        {
          id: 'plotArea',
          type: 'number',
          label: 'Plot Area (sq.ft)',
          placeholder: 'Ex - 1500',
          conditional: (data) => data.propertyType === 'plot' || data.propertyType === 'independent_house',
          validation: { min: 100 }
        }
      ]
    },
    {
      id: 'purchase_details',
      title: 'Purchase/Construction Details',
      icon: Building2,
      description: 'Details about your purchase or construction plans',
      fields: [
        {
          id: 'loanPurpose',
          type: 'radio',
          label: 'Loan Purpose',
          validation: { required: true },
          options: [
            { value: 'purchase', label: 'Purchase of Property' },
            { value: 'construction', label: 'Construction on Own Land' },
            { value: 'renovation', label: 'Renovation/Extension' },
            { value: 'lap', label: 'Loan Against Property' }
          ]
        },
        {
          id: 'sellerName',
          type: 'text',
          label: 'Seller/Builder Name',
          placeholder: 'Ex - ABC Builders Pvt Ltd',
          conditional: (data) => data.loanPurpose === 'purchase'
        },
        {
          id: 'sellerPAN',
          type: 'text',
          label: 'Seller/Builder PAN',
          placeholder: 'Ex - ABCDE1234F',
          conditional: (data) => data.loanPurpose === 'purchase',
          validation: { pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$' }
        },
        {
          id: 'registryDate',
          type: 'date',
          label: 'Expected Registry Date',
          conditional: (data) => data.loanPurpose === 'purchase'
        },
        {
          id: 'ownContribution',
          type: 'currency',
          label: 'Own Contribution/Down Payment (₹)',
          placeholder: 'Ex - 10,00,000',
          validation: { required: true, min: 0 }
        },
        {
          id: 'loanTenureRequired',
          type: 'number',
          label: 'Loan Tenure Required (Years)',
          placeholder: 'Ex - 20',
          validation: { required: true, min: 5, max: 30 }
        }
      ]
    },
    {
      id: 'co_applicant',
      title: 'Co-Applicant Details',
      icon: Users,
      description: 'Add co-applicant to increase loan eligibility',
      fields: [
        {
          id: 'hasCoApplicant',
          type: 'radio',
          label: 'Do you have a co-applicant?',
          validation: { required: true },
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'coApplicantName',
          type: 'text',
          label: 'Co-Applicant Full Name',
          placeholder: 'Ex - Rajesh Kumar',
          conditional: (data) => data.hasCoApplicant === 'yes',
          validation: { required: true }
        },
        {
          id: 'coApplicantRelation',
          type: 'select',
          label: 'Relationship with Applicant',
          conditional: (data) => data.hasCoApplicant === 'yes',
          validation: { required: true },
          options: [
            { value: 'spouse', label: 'Spouse' },
            { value: 'parent', label: 'Parent' },
            { value: 'sibling', label: 'Sibling' },
            { value: 'child', label: 'Child' },
            { value: 'friend', label: 'Friend' },
            { value: 'other', label: 'Other' }
          ]
        },
        {
          id: 'coApplicantDOB',
          type: 'date',
          label: 'Co-Applicant Date of Birth',
          conditional: (data) => data.hasCoApplicant === 'yes',
          validation: { required: true }
        },
        {
          id: 'coApplicantPAN',
          type: 'text',
          label: 'Co-Applicant PAN Card',
          placeholder: 'Ex - ABCDE1234F',
          conditional: (data) => data.hasCoApplicant === 'yes',
          validation: { pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$' }
        },
        {
          id: 'coApplicantEmployment',
          type: 'select',
          label: 'Co-Applicant Employment Type',
          conditional: (data) => data.hasCoApplicant === 'yes',
          options: [
            { value: 'salaried', label: 'Salaried' },
            { value: 'self_employed_professional', label: 'Self-Employed Professional' },
            { value: 'self_employed_business', label: 'Self-Employed Business' },
            { value: 'retired', label: 'Retired' },
            { value: 'homemaker', label: 'Homemaker' }
          ]
        },
        {
          id: 'coApplicantIncome',
          type: 'currency',
          label: 'Co-Applicant Monthly Income (₹)',
          placeholder: 'Ex - 40,000',
          conditional: (data) => data.hasCoApplicant === 'yes' && 
                                  ['salaried', 'self_employed_professional', 'self_employed_business'].includes(data.coApplicantEmployment)
        }
      ]
    },
    {
      id: 'employment_income',
      title: 'Income & Employment',
      icon: Briefcase,
      description: 'Your employment and income details',
      fields: [
        {
          id: 'employmentType',
          type: 'radio',
          label: 'Employment Type',
          validation: { required: true },
          options: [
            { value: 'salaried', label: 'Salaried' },
            { value: 'self_employed_professional', label: 'Self-Employed Professional' },
            { value: 'self_employed_business', label: 'Self-Employed Business' }
          ]
        },
        // Salaried Fields
        {
          id: 'companyName',
          type: 'text',
          label: 'Company Name',
          placeholder: 'Ex - Infosys Ltd',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'designation',
          type: 'text',
          label: 'Designation',
          placeholder: 'Ex - Senior Manager',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'companyType',
          type: 'select',
          label: 'Company Type',
          conditional: (data) => data.employmentType === 'salaried',
          options: [
            { value: 'mnc', label: 'MNC' },
            { value: 'public_sector', label: 'Public Sector/Govt' },
            { value: 'private_limited', label: 'Private Limited' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'proprietorship', label: 'Proprietorship' }
          ]
        },
        {
          id: 'dateOfJoining',
          type: 'date',
          label: 'Date of Joining Current Company',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'totalExperience',
          type: 'number',
          label: 'Total Work Experience (Years)',
          placeholder: 'Ex - 8',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { min: 0, max: 50 }
        },
        {
          id: 'officeAddress',
          type: 'textarea',
          label: 'Office Address',
          placeholder: 'Complete office address',
          conditional: (data) => data.employmentType === 'salaried'
        },
        // Self-Employed Fields
        {
          id: 'businessName',
          type: 'text',
          label: 'Business/Firm Name',
          placeholder: 'Ex - ABC Enterprises',
          conditional: (data) => isSelfEmployed(data),
          validation: { required: true }
        },
        {
          id: 'natureOfBusiness',
          type: 'text',
          label: 'Nature of Business',
          placeholder: 'Ex - Textile Trading',
          conditional: (data) => isSelfEmployed(data),
          validation: { required: true }
        },
        {
          id: 'businessVintage',
          type: 'number',
          label: 'Business Vintage (Years)',
          placeholder: 'Ex - 5',
          conditional: (data) => isSelfEmployed(data),
          validation: { required: true, min: 0 }
        },
        {
          id: 'businessAddress',
          type: 'textarea',
          label: 'Business Address',
          placeholder: 'Complete business address',
          conditional: (data) => isSelfEmployed(data)
        },
        {
          id: 'gstNumber',
          type: 'text',
          label: 'GST Number (if applicable)',
          placeholder: 'Ex - 27XXXXX1234X1Z5',
          conditional: (data) => isSelfEmployed(data)
        },
        // Common Income Fields
        {
          id: 'monthlyIncome',
          type: 'currency',
          label: 'Monthly Net Income/Salary (₹)',
          placeholder: 'Ex - 75,000',
          validation: { required: true, min: 10000 }
        },
        {
          id: 'annualIncome',
          type: 'currency',
          label: 'Annual Gross Income (₹)',
          placeholder: 'Ex - 12,00,000',
          validation: { required: true, min: 100000 }
        },
        {
          id: 'otherIncome',
          type: 'currency',
          label: 'Other Income (Rental/Interest) (₹)',
          placeholder: 'Ex - 15,000'
        }
      ]
    },
    {
      id: 'existing_liabilities',
      title: 'Existing Loans & Liabilities',
      icon: CreditCard,
      description: 'Details of your current EMIs and obligations',
      fields: [
        {
          id: 'hasExistingLoans',
          type: 'radio',
          label: 'Do you have any existing loans/EMIs?',
          validation: { required: true },
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'homeLoanEMI',
          type: 'currency',
          label: 'Existing Home Loan EMI (₹)',
          placeholder: 'Ex - 25,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        },
        {
          id: 'personalLoanEMI',
          type: 'currency',
          label: 'Personal Loan EMI (₹)',
          placeholder: 'Ex - 10,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        },
        {
          id: 'carLoanEMI',
          type: 'currency',
          label: 'Car/Vehicle Loan EMI (₹)',
          placeholder: 'Ex - 15,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        },
        {
          id: 'creditCardEMI',
          type: 'currency',
          label: 'Credit Card EMI (₹)',
          placeholder: 'Ex - 5,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        },
        {
          id: 'otherLoans',
          type: 'textarea',
          label: 'Other Loans (Describe)',
          placeholder: 'Business loan, Education loan, etc.',
          conditional: (data) => data.hasExistingLoans === 'yes'
        },
        {
          id: 'totalMonthlyEMI',
          type: 'currency',
          label: 'Total Monthly EMI (₹)',
          placeholder: 'Ex - 55,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        }
      ]
    },
    {
      id: 'banking_details',
      title: 'Banking & Financial Details',
      icon: Building,
      description: 'Your primary banking relationship',
      fields: [
        {
          id: 'primaryBankName',
          type: 'text',
          label: 'Primary Bank Name',
          placeholder: 'Ex - HDFC Bank',
          validation: { required: true }
        },
        {
          id: 'accountType',
          type: 'select',
          label: 'Account Type',
          validation: { required: true },
          options: [
            { value: 'savings', label: 'Savings Account' },
            { value: 'current', label: 'Current Account' },
            { value: 'salary', label: 'Salary Account' }
          ]
        },
        {
          id: 'accountVintage',
          type: 'number',
          label: 'Account Age (Years)',
          placeholder: 'Ex - 5',
          validation: { required: true, min: 0 }
        },
        {
          id: 'monthlyBankingTurnover',
          type: 'currency',
          label: 'Average Monthly Banking (₹)',
          placeholder: 'Ex - 2,00,000',
          conditional: (data) => isSelfEmployed(data)
        },
        {
          id: 'itrFiled',
          type: 'radio',
          label: 'ITR Filed for Last 2 Years?',
          validation: { required: true },
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'last2YearsITR',
          type: 'textarea',
          label: 'Last 2 Years ITR Amount (₹)',
          placeholder: 'Ex - 2023: 10,00,000\n2024: 12,00,000',
          conditional: (data) => data.itrFiled === 'yes'
        }
      ]
    },
    {
      id: 'documents_checklist',
      title: 'Required Documents',
      icon: FileCheck,
      description: 'Checklist of documents needed for loan processing',
      fields: [
        {
          id: 'documentsChecklist',
          type: 'checkbox',
          label: 'Check documents you have ready',
          options: [
            { value: 'pan_card', label: 'PAN Card' },
            { value: 'aadhar_card', label: 'Aadhar Card' },
            { value: 'bank_statements_6m', label: 'Bank Statements (Last 6 months)' },
            { value: 'salary_slips_3m', label: 'Salary Slips (Last 3 months)' },
            { value: 'form16', label: 'Form 16' },
            { value: 'itr_2years', label: 'ITR (Last 2 years)' },
            { value: 'property_papers', label: 'Property Documents' },
            { value: 'sale_agreement', label: 'Sale Agreement' },
            { value: 'builder_agreement', label: 'Builder Agreement' },
            { value: 'business_proof', label: 'Business Proof (GST/Shop License)' },
            { value: 'property_tax_receipt', label: 'Property Tax Receipt' }
          ]
        }
      ]
    }
  ],
  requiredDocuments: [
    'PAN Card',
    'Aadhar Card',
    'Bank Statements (6 months)',
    'Salary Slips (3 months) / ITR (2 years)',
    'Property Documents',
    'Sale Agreement'
  ]
};

// LAP Questionnaire (Same as Home Loan)
const lapQuestionnaire: LoanQuestionnaire = {
  ...homeLoanQuestionnaire,
  loanType: 'lap'
};

// Personal Loan Questionnaire (Simplified)
const personalLoanQuestionnaire: LoanQuestionnaire = {
  loanType: 'personal_loan',
  sections: [
    {
      id: 'loan_purpose',
      title: 'Loan Purpose',
      icon: Target,
      description: 'What is the loan for?',
      fields: [
        {
          id: 'loanPurpose',
          type: 'radio',
          label: 'What is the loan for?',
          validation: { required: true },
          options: [
            { value: 'debt_consolidation', label: 'Debt Consolidation' },
            { value: 'medical_emergency', label: 'Medical Emergency' },
            { value: 'wedding', label: 'Wedding Expenses' },
            { value: 'home_renovation', label: 'Home Renovation' },
            { value: 'travel', label: 'Travel/Vacation' },
            { value: 'business', label: 'Business Purpose' },
            { value: 'education', label: 'Education' },
            { value: 'other', label: 'Other' }
          ]
        },
        {
          id: 'loanPurposeDetails',
          type: 'textarea',
          label: 'Provide details',
          placeholder: 'Brief description of loan purpose',
          validation: { required: true, minLength: 10 }
        },
        {
          id: 'loanAmountRequired',
          type: 'currency',
          label: 'Loan Amount Required (₹)',
          placeholder: 'Ex - 5,00,000',
          validation: { required: true, min: 50000 }
        },
        {
          id: 'loanTenureRequired',
          type: 'number',
          label: 'Loan Tenure Required (Months)',
          placeholder: 'Ex - 36',
          validation: { required: true, min: 12, max: 60 }
        }
      ]
    },
    {
      id: 'employment_income',
      title: 'Employment & Income',
      icon: Briefcase,
      description: 'Your employment and income details',
      fields: [
        {
          id: 'employmentType',
          type: 'radio',
          label: 'Employment Type',
          validation: { required: true },
          options: [
            { value: 'salaried', label: 'Salaried' },
            { value: 'self_employed_professional', label: 'Self-Employed Professional' },
            { value: 'self_employed_business', label: 'Self-Employed Business' }
          ]
        },
        {
          id: 'companyName',
          type: 'text',
          label: 'Company Name',
          placeholder: 'Ex - Infosys Ltd',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'designation',
          type: 'text',
          label: 'Designation',
          placeholder: 'Ex - Senior Manager',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'dateOfJoining',
          type: 'date',
          label: 'Date of Joining Current Company',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'businessName',
          type: 'text',
          label: 'Business/Firm Name',
          placeholder: 'Ex - ABC Enterprises',
          conditional: (data) => isSelfEmployed(data),
          validation: { required: true }
        },
        {
          id: 'businessVintage',
          type: 'number',
          label: 'Business Vintage (Years)',
          placeholder: 'Ex - 5',
          conditional: (data) => isSelfEmployed(data),
          validation: { required: true, min: 0 }
        },
        {
          id: 'monthlyIncome',
          type: 'currency',
          label: 'Monthly Net Income/Salary (₹)',
          placeholder: 'Ex - 75,000',
          validation: { required: true, min: 10000 }
        },
        {
          id: 'annualIncome',
          type: 'currency',
          label: 'Annual Gross Income (₹)',
          placeholder: 'Ex - 12,00,000',
          validation: { required: true, min: 100000 }
        }
      ]
    },
    {
      id: 'existing_liabilities',
      title: 'Existing Loans & Credit Cards',
      icon: CreditCard,
      description: 'Current financial obligations',
      fields: [
        {
          id: 'hasExistingLoans',
          type: 'radio',
          label: 'Do you have any existing loans/EMIs?',
          validation: { required: true },
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'totalMonthlyEMI',
          type: 'currency',
          label: 'Total Monthly EMI (₹)',
          placeholder: 'Ex - 25,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        },
        {
          id: 'creditCardDues',
          type: 'currency',
          label: 'Outstanding Credit Card Dues (₹)',
          placeholder: 'Ex - 50,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        }
      ]
    },
    {
      id: 'banking_details',
      title: 'Banking Details',
      icon: Building,
      description: 'Your primary bank account',
      fields: [
        {
          id: 'primaryBankName',
          type: 'text',
          label: 'Primary Bank Name',
          placeholder: 'Ex - HDFC Bank',
          validation: { required: true }
        },
        {
          id: 'accountType',
          type: 'select',
          label: 'Account Type',
          validation: { required: true },
          options: [
            { value: 'savings', label: 'Savings Account' },
            { value: 'salary', label: 'Salary Account' }
          ]
        },
        {
          id: 'accountVintage',
          type: 'number',
          label: 'Account Age (Years)',
          placeholder: 'Ex - 3',
          validation: { required: true, min: 0 }
        }
      ]
    }
  ],
  requiredDocuments: [
    'PAN Card',
    'Aadhar Card',
    'Bank Statements (3 months)',
    'Salary Slips (2 months)',
    'Employment Proof'
  ]
};

// Business Loan Questionnaire
const businessLoanQuestionnaire: LoanQuestionnaire = {
  loanType: 'business_loan',
  sections: [
    {
      id: 'business_info',
      title: 'Business Information',
      icon: Building2,
      description: 'Details about your business',
      fields: [
        {
          id: 'businessName',
          type: 'text',
          label: 'Business/Company Name',
          placeholder: 'Ex - ABC Enterprises',
          validation: { required: true }
        },
        {
          id: 'businessType',
          type: 'select',
          label: 'Business Type',
          validation: { required: true },
          options: [
            { value: 'proprietorship', label: 'Proprietorship' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'llp', label: 'LLP' },
            { value: 'private_limited', label: 'Private Limited' },
            { value: 'public_limited', label: 'Public Limited' }
          ]
        },
        {
          id: 'industry',
          type: 'select',
          label: 'Industry/Sector',
          validation: { required: true },
          options: [
            { value: 'manufacturing', label: 'Manufacturing' },
            { value: 'trading', label: 'Trading' },
            { value: 'services', label: 'Services' },
            { value: 'retail', label: 'Retail' },
            { value: 'hospitality', label: 'Hospitality' },
            { value: 'healthcare', label: 'Healthcare' },
            { value: 'education', label: 'Education' },
            { value: 'it', label: 'IT/Software' },
            { value: 'construction', label: 'Construction' },
            { value: 'agriculture', label: 'Agriculture' }
          ]
        },
        {
          id: 'businessVintage',
          type: 'number',
          label: 'Business Operating Since (Years)',
          placeholder: 'Ex - 5',
          validation: { required: true, min: 0 }
        },
        {
          id: 'numberOfEmployees',
          type: 'number',
          label: 'Number of Employees',
          placeholder: 'Ex - 25',
          validation: { min: 0 }
        },
        {
          id: 'registrationNumber',
          type: 'text',
          label: 'Registration Number (CIN/LLPIN/ROC)',
          placeholder: 'Ex - U12345KA2015PTC123456'
        },
        {
          id: 'gstNumber',
          type: 'text',
          label: 'GST Number',
          placeholder: 'Ex - 29XXXXX1234X1Z5',
          validation: { required: true }
        },
        {
          id: 'businessAddress',
          type: 'textarea',
          label: 'Registered Business Address',
          placeholder: 'Complete business address',
          validation: { required: true, minLength: 10 }
        }
      ]
    },
    {
      id: 'financial_performance',
      title: 'Financial Performance',
      icon: TrendingUp,
      description: 'Business financial details',
      fields: [
        {
          id: 'annualTurnover',
          type: 'currency',
          label: 'Annual Turnover (₹)',
          placeholder: 'Ex - 50,00,000',
          validation: { required: true, min: 100000 }
        },
        {
          id: 'monthlyTurnover',
          type: 'currency',
          label: 'Average Monthly Turnover (₹)',
          placeholder: 'Ex - 4,00,000'
        },
        {
          id: 'profitMargin',
          type: 'number',
          label: 'Profit Margin (%)',
          placeholder: 'Ex - 15',
          validation: { min: 0, max: 100 }
        },
        {
          id: 'itrFiledLast3Years',
          type: 'radio',
          label: 'ITR Filed for Last 3 Years?',
          validation: { required: true },
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'last3YearsITR',
          type: 'textarea',
          label: 'Last 3 Years ITR Amount (₹)',
          placeholder: 'Ex - 2022: 8,00,000\n2023: 10,00,000\n2024: 12,00,000',
          conditional: (data) => data.itrFiledLast3Years === 'yes'
        }
      ]
    },
    {
      id: 'loan_requirement',
      title: 'Loan Requirement',
      icon: Target,
      description: 'Business loan details',
      fields: [
        {
          id: 'loanPurpose',
          type: 'select',
          label: 'Loan Purpose',
          validation: { required: true },
          options: [
            { value: 'working_capital', label: 'Working Capital' },
            { value: 'machinery', label: 'Machinery Purchase' },
            { value: 'expansion', label: 'Business Expansion' },
            { value: 'inventory', label: 'Inventory Purchase' },
            { value: 'debt_consolidation', label: 'Debt Consolidation' },
            { value: 'other', label: 'Other' }
          ]
        },
        {
          id: 'loanAmount',
          type: 'currency',
          label: 'Loan Amount Required (₹)',
          placeholder: 'Ex - 25,00,000',
          validation: { required: true, min: 100000 }
        },
        {
          id: 'repaymentCapacity',
          type: 'currency',
          label: 'Monthly Repayment Capacity (₹)',
          placeholder: 'Ex - 1,50,000',
          validation: { required: true, min: 10000 }
        }
      ]
    },
    {
      id: 'business_banking',
      title: 'Business Banking',
      icon: Building,
      description: 'Business bank account details',
      fields: [
        {
          id: 'primaryBankName',
          type: 'text',
          label: 'Primary Business Bank',
          placeholder: 'Ex - ICICI Bank',
          validation: { required: true }
        },
        {
          id: 'accountVintage',
          type: 'number',
          label: 'Account Age (Years)',
          placeholder: 'Ex - 5',
          validation: { required: true, min: 0 }
        },
        {
          id: 'monthlyBankingTurnover',
          type: 'currency',
          label: 'Average Monthly Banking (₹)',
          placeholder: 'Ex - 10,00,000',
          validation: { required: true, min: 10000 }
        },
        {
          id: 'hasCC_OD',
          type: 'radio',
          label: 'Existing CC/OD Limit?',
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'ccOdLimit',
          type: 'currency',
          label: 'CC/OD Limit (₹)',
          placeholder: 'Ex - 5,00,000',
          conditional: (data) => data.hasCC_OD === 'yes'
        }
      ]
    }
  ],
  requiredDocuments: [
    'Business Registration',
    'GST Returns (12 months)',
    'Bank Statements (12 months)',
    'ITR (3 years)',
    'Financial Statements',
    'PAN Card',
    'Aadhar Card'
  ]
};

// Car Loan Questionnaire
const carLoanQuestionnaire: LoanQuestionnaire = {
  loanType: 'car_loan',
  sections: [
    {
      id: 'vehicle_details',
      title: 'Vehicle Details',
      icon: Target,
      description: 'Details about the vehicle you want to purchase',
      fields: [
        {
          id: 'vehicleType',
          type: 'radio',
          label: 'Vehicle Type',
          validation: { required: true },
          options: [
            { value: 'new_car', label: 'New Car' },
            { value: 'used_car', label: 'Used Car' }
          ]
        },
        {
          id: 'vehicleMake',
          type: 'text',
          label: 'Vehicle Make',
          placeholder: 'Ex - Maruti Suzuki',
          validation: { required: true }
        },
        {
          id: 'vehicleModel',
          type: 'text',
          label: 'Vehicle Model',
          placeholder: 'Ex - Swift VXI',
          validation: { required: true }
        },
        {
          id: 'vehicleVariant',
          type: 'text',
          label: 'Vehicle Variant',
          placeholder: 'Ex - Top Model',
        },
        {
          id: 'manufacturingYear',
          type: 'number',
          label: 'Manufacturing Year',
          placeholder: 'Ex - 2024',
          validation: { required: true, min: 2000 }
        },
        {
          id: 'onRoadPrice',
          type: 'currency',
          label: 'On-Road Price (₹)',
          placeholder: 'Ex - 8,00,000',
          validation: { required: true, min: 100000 }
        },
        {
          id: 'downPayment',
          type: 'currency',
          label: 'Down Payment (₹)',
          placeholder: 'Ex - 2,00,000',
          validation: { required: true, min: 0 }
        },
        {
          id: 'loanTenureRequired',
          type: 'number',
          label: 'Loan Tenure Required (Years)',
          placeholder: 'Ex - 5',
          validation: { required: true, min: 1, max: 7 }
        }
      ]
    },
    {
      id: 'employment_income',
      title: 'Employment & Income',
      icon: Briefcase,
      description: 'Your employment and income details',
      fields: [
        {
          id: 'employmentType',
          type: 'radio',
          label: 'Employment Type',
          validation: { required: true },
          options: [
            { value: 'salaried', label: 'Salaried' },
            { value: 'self_employed_professional', label: 'Self-Employed Professional' },
            { value: 'self_employed_business', label: 'Self-Employed Business' }
          ]
        },
        {
          id: 'companyName',
          type: 'text',
          label: 'Company Name',
          placeholder: 'Ex - Infosys Ltd',
          conditional: (data) => data.employmentType === 'salaried',
          validation: { required: true }
        },
        {
          id: 'designation',
          type: 'text',
          label: 'Designation',
          placeholder: 'Ex - Senior Manager',
          conditional: (data) => data.employmentType === 'salaried'
        },
        {
          id: 'businessName',
          type: 'text',
          label: 'Business/Firm Name',
          placeholder: 'Ex - ABC Enterprises',
          conditional: (data) => isSelfEmployed(data),
          validation: { required: true }
        },
        {
          id: 'monthlyIncome',
          type: 'currency',
          label: 'Monthly Net Income (₹)',
          placeholder: 'Ex - 75,000',
          validation: { required: true, min: 10000 }
        }
      ]
    },
    {
      id: 'existing_liabilities',
      title: 'Existing Loans',
      icon: CreditCard,
      description: 'Current financial obligations',
      fields: [
        {
          id: 'hasExistingLoans',
          type: 'radio',
          label: 'Do you have any existing loans/EMIs?',
          validation: { required: true },
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
          ]
        },
        {
          id: 'totalMonthlyEMI',
          type: 'currency',
          label: 'Total Monthly EMI (₹)',
          placeholder: 'Ex - 15,000',
          conditional: (data) => data.hasExistingLoans === 'yes'
        }
      ]
    }
  ],
  requiredDocuments: [
    'PAN Card',
    'Aadhar Card',
    'Salary Slips (2 months)',
    'Bank Statements (3 months)',
    'Vehicle Quotation/Proforma Invoice'
  ]
};

// Export questionnaire getter function
export const getQuestionnaireForLoanType = (loanType: LoanType): LoanQuestionnaire => {
  switch (loanType) {
    case 'home_loan':
      return homeLoanQuestionnaire;
    case 'lap':
      return lapQuestionnaire;
    case 'personal_loan':
      return personalLoanQuestionnaire;
    case 'business_loan':
      return businessLoanQuestionnaire;
    case 'car_loan':
      return carLoanQuestionnaire;
    default:
      return homeLoanQuestionnaire; // Default fallback
  }
};

// Validation helper function
export const validateField = (field: QuestionnaireField, value: any): string | null => {
  if (field.validation?.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    return 'This field is required';
  }

  if (field.type === 'number' && value) {
    const numValue = Number(value);
    if (field.validation?.min !== undefined && numValue < field.validation.min) {
      return `Minimum value is ${field.validation.min}`;
    }
    if (field.validation?.max !== undefined && numValue > field.validation.max) {
      return `Maximum value is ${field.validation.max}`;
    }
  }

  if (field.type === 'text' && value && typeof value === 'string') {
    if (field.validation?.minLength && value.length < field.validation.minLength) {
      return `Minimum length is ${field.validation.minLength} characters`;
    }
    if (field.validation?.maxLength && value.length > field.validation.maxLength) {
      return `Maximum length is ${field.validation.maxLength} characters`;
    }
    if (field.validation?.pattern) {
      const regex = new RegExp(field.validation.pattern);
      if (!regex.test(value)) {
        return 'Invalid format';
      }
    }
  }

  return null;
};

// Indian number formatting helpers
export const formatIndianNumber = (num: number | string): string => {
  if (!num && num !== 0) return '';
  
  const numStr = String(num).replace(/[^0-9.]/g, '');
  const parts = numStr.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] ? `.${parts[1]}` : '';
  
  // Indian number formatting (lakhs and crores)
  const lastThree = integerPart.substring(integerPart.length - 3);
  const otherNumbers = integerPart.substring(0, integerPart.length - 3);
  const formatted = otherNumbers !== '' 
    ? otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree 
    : lastThree;
  
  return formatted + decimalPart;
};

export const parseIndianNumber = (str: string): number => {
  if (!str) return 0;
  const cleaned = str.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
};
